import {
  useFeatureFlags,
  useFolderStructure,
} from '@src/stores-v2/StoreContext';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { ReactNode } from 'react';

/**
 * Encapsulates folder-picker state for the story upload modal:
 * hydration, selection tracking, tree-walk options, and post-creation assignment.
 */
export function useFolderPicker(showcaseId: string | undefined) {
  const folderStore = useFolderStructure();
  const { enableStoryFolders } = useFeatureFlags();

  // Make sure the folder structure is loaded so the picker can appear even
  // when the modal is opened outside the folder view (Home / Recent view).
  // Use the reliable CMA-backed loadStructure — the CDA folderStructure
  // snapshot returns stale/null for this field. A no-op once the folder view
  // has hydrated this showcase, so it doesn't disturb its navigation.
  useEffect(() => {
    if (showcaseId) void folderStore.loadStructure(showcaseId);
  }, [showcaseId, folderStore]);

  // Default the picker to the folder the user is currently viewing, so a
  // story uploaded from inside a subfolder is filed there. Tracked as a
  // default (synced from the store) until the user explicitly picks, so it
  // settles to the right folder regardless of mount timing.
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(
    folderStore.currentFolderId,
  );
  const userPickedFolderRef = useRef(false);
  useEffect(() => {
    if (!userPickedFolderRef.current) {
      setSelectedFolderId(folderStore.currentFolderId);
    }
  }, [folderStore.currentFolderId]);

  const handleSelectFolder = (id: string | null) => {
    userPickedFolderRef.current = true;
    setSelectedFolderId(id);
  };

  // Mirror the selection into a ref so memoized callbacks in the consumer
  // can read the latest choice without it being in their dep array.
  const selectedFolderIdRef = useRef<string | null>(selectedFolderId);
  useEffect(() => {
    selectedFolderIdRef.current = selectedFolderId;
  }, [selectedFolderId]);
  const folderAssignedRef = useRef<boolean>(false);

  const showFolderPicker =
    enableStoryFolders && folderStore.structure.folders.length > 0;

  // Flatten the folder tree depth-first (mirrors MoveStoryDialog), prefixed
  // by a "No folder" (root) option. Nested folders are indented and prefixed
  // with a branch glyph + dimmed so they read clearly as subfolders.
  const folderOptions = useMemo<
    { caption: ReactNode; value: string | null }[]
  >(() => {
    const options: { caption: ReactNode; value: string | null }[] = [
      { caption: 'None', value: null },
    ];
    const byParent = folderStore.foldersByParent;
    const walk = (parentId: string | null, depth: number) => {
      for (const f of byParent.get(parentId) ?? []) {
        options.push({
          caption:
            depth === 0 ? (
              f.name
            ) : (
              <span style={{ paddingLeft: `${depth * 16}px`, opacity: 0.65 }}>
                ↳ {f.name}
              </span>
            ),
          value: f.id,
        });
        walk(f.id, depth + 1);
      }
    };
    walk(null, 0);
    return options;
  }, [folderStore.foldersByParent]);

  /**
   * File the story into the chosen folder. Fire and forget — a failed move
   * is logged by the store, never blocks upload. Also navigates the folder
   * view into the target folder so Story Manager opens there post-upload.
   */
  const assignStoryToFolder = (storyId: string) => {
    const folderId = selectedFolderIdRef.current;
    if (folderId && !folderAssignedRef.current) {
      folderAssignedRef.current = true;
      void folderStore.moveStoryToFolder(storyId, folderId);
      // Land the user inside the folder they filed into, so Story
      // Manager opens that folder (not root) once the modal closes.
      folderStore.enterFolder(folderId);
    }
  };

  return {
    showFolderPicker,
    folderOptions,
    selectedFolderId,
    setSelectedFolderId: handleSelectFolder,
    assignStoryToFolder,
  };
}
